import api from '~/services/api';
import { API_NODE_URL, API_BASE_URL } from '~/constants';
import toast from '~/services/toast';
import DialogHeader from '../../components/Impressao/DialogHeader';
import * as Mui from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import download from 'downloadjs';

const ModalBoletoConvenio = ({ data, showModal, onClose }) => {
  const [contas, setContas] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [selectedIds, setSelectedIds] = useState({
    contaId: '',
    convenioId: ''
  });
  const [loading, setLoading] = useState({
    contas: false,
    convenios: false,
    geracao: false
  });

  const fetchContas = useCallback(async () => {
    if (!data?.pagamentoId) return;

    setLoading(prev => ({ ...prev, contas: true }));
    try {
      const response = await api.get(
        `${API_BASE_URL}/v1/financeiro/meiopagamento/${data.pagamentoId}`
      );

      const contasExtraidas =
        response?.data?.finMeiopagamentoPortadores?.map(portador => ({
          id: portador.finConta.id,
          nome: portador.finConta.descricao,
          banco: portador.finConta.bancoContaCodEmpresa,
          ...portador.finConta
        })) || [];

      setContas(contasExtraidas);

      if (contasExtraidas.length === 1) {
        setSelectedIds(prev => ({ ...prev, contaId: contasExtraidas[0].id }));
      }
    } catch (error) {
      toast.error('Erro ao buscar contas');
    } finally {
      setLoading(prev => ({ ...prev, contas: false }));
    }
  }, [data?.pagamentoId]);

  const fetchConvenios = useCallback(async contaId => {
    if (!contaId) return;

    setLoading(prev => ({ ...prev, convenios: true }));
    try {
      const { data: response } = await api.get(
        `${API_NODE_URL}/financeiro/conta/${contaId}/convenio`
      );

      const conveniosExtraidos =
        response?.dados?.map(convenio => ({
          id: convenio.id,
          nome: `Convênio ${convenio.convenio}`,
          codigo: convenio.convenio,
          ...convenio
        })) || [];

      setConvenios(conveniosExtraidos);

      if (conveniosExtraidos.length === 1) {
        setSelectedIds(prev => ({ ...prev, convenioId: conveniosExtraidos[0].id }));
      } else {
        setSelectedIds(prev => ({ ...prev, convenioId: '' }));
      }
    } catch (error) {
      toast.error('Erro ao buscar convênios');
    } finally {
      setLoading(prev => ({ ...prev, convenios: false }));
    }
  }, []);

  const handleClose = () => {
    setContas([]);
    setConvenios([]);
    setSelectedIds({ contaId: '', convenioId: '' });
    onClose();
  };

  const handleBuscaTitulos = async finTitulosIds => {
    setLoading(prev => ({ ...prev, geracao: true }));
    try {
      const { data: response } = await api.post(
        `${API_NODE_URL}/financeiro/contasareceber/busca-titulo/`,
        {
          titulos: finTitulosIds
        }
      );
      return response.dados;
    } catch (error) {
      console.error('Erro:', error.response?.data || error.message);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, geracao: false }));
    }
  };

  const handleGerarArquivoRemessa = async (boletosEmitidos) => {
    const idsIntegracao = boletosEmitidos.map(boleto => boleto.integradorBoletoId);
    
    setLoading(prev => ({ ...prev, arquivoRemessa: true }));
    try {
      const { data: response } = await api.post(
        `${API_NODE_URL}/financeiro/contasareceber/boleto/arquivo-remessa`,
        { IDsIntegracao: idsIntegracao }
      );
  
      if (response.dados?._sucesso?.[0]) {
        const remessaDecodificada = atob(response.dados._sucesso[0].remessa);
        download(
          remessaDecodificada,
          response.dados._sucesso[0].arquivo,
          'text/plain'
        );
        toast.success('Arquivo remessa gerado com sucesso!');
      }
    } catch (error) {
      toast.error(`Erro ao gerar arquivo remessa: ${error.response?.data?.mensagem || error.message}`);
      console.error('Erro no arquivo remessa:', error);
    } finally {
      setLoading(prev => ({ ...prev, arquivoRemessa: false }));
    }
  };

  const handleGerarBoleto = async () => {
    const { contaId, convenioId } = selectedIds;
    if (!contaId || !convenioId) return;

    const idsTitulos = await handleBuscaTitulos(data.finTitulosId);

    const titulosIds = idsTitulos.map(titulo => titulo.id);

    setLoading(prev => ({ ...prev, geracao: true }));
    try {
      const { data: response } = await api.post(
        `${API_NODE_URL}/financeiro/contasareceber/boleto/cadastrar`,
        {
          finContaId: contaId,
          finConvenioId: convenioId,
          titulos: titulosIds
        }
      );

      if (response.dados?.boletosEmitidos.length > 0) {
        response.dados.boletosEmitidos.forEach(boleto => {
          if (boleto?.integradorBoletoUrl) {
            window.open(boleto.integradorBoletoUrl, '_blank', 'noopener,noreferrer');
          }
        });
        toast.success(`${response.dados.boletosEmitidos.length} boleto(s) gerado(s) com sucesso!`);

        await handleGerarArquivoRemessa(response.dados.boletosEmitidos);

        onClose();
      }

      if (response.dados?.boletosComFalha.length > 0) {
        const boletoComFalha = response.dados?.boletosComFalha[0];
        if (boletoComFalha?.boletoProtocolado?.situacao === 'FALHA') {
          toast.error(`Erro ao cadastrar boleto, ${boletoComFalha?.boletoProtocolado?.motivo}`);
          return false;
        }
      }

      if (response.dados?.boletosPendentes.length > 0) {
        toast.error(`${response.dados.boletosPendentes.length} boleto(s) pendentes. Consulte a tela de gerenciamento de Boletos `);
      }
    } catch (error) {
      toast.error(`Erro ao gerar boleto, ${error?.response?.data?.mensagem}`);
      console.error('Erro ao gerar boleto:', error);
    } finally {
      setLoading(prev => ({ ...prev, geracao: false }));
    }
  };

  useEffect(() => {
    if (showModal?.show) {
      fetchContas();
    }

    return () => {
      if (!showModal?.show) {
        setContas([]);
        setConvenios([]);
        setSelectedIds({ contaId: '', convenioId: '' });
      }
    };
  }, [showModal?.show, fetchContas]);

  useEffect(() => {
    if (selectedIds.contaId) {
      fetchConvenios(selectedIds.contaId);
    }
  }, [selectedIds.contaId, fetchConvenios]);

  const globalLoading = loading.contas || loading.convenios;

  return (
    <>
      <Mui.Dialog
        open={showModal.show}
        onKeyDown={key => {
          if (key.code === 'Escape') {
            handleClose();
          }
        }}
        maxWidth={'md'}
        fullWidth={true}
      >
        <DialogHeader title="Cadastrar Boleto" onClose={onClose} />
        <Mui.DialogContent>
          {globalLoading && (
            <Mui.Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor="rgba(0, 0, 0, 0.1)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={1}
            >
              <Mui.CircularProgress size={60} />
            </Mui.Box>
          )}
          <Mui.FormControl fullWidth style={{ marginTop: 16 }}>
            <Mui.InputLabel>Selecione a Conta</Mui.InputLabel>
            <Mui.Select
              value={selectedIds.contaId}
              onChange={e => setSelectedIds(prev => ({ ...prev, contaId: e.target.value }))}
              disabled={globalLoading}
            >
              {contas.map(conta => (
                <Mui.MenuItem key={conta.id} value={conta.id}>
                  {conta.nome} - {conta.banco}
                </Mui.MenuItem>
              ))}
            </Mui.Select>
          </Mui.FormControl>

          <Mui.FormControl fullWidth style={{ marginTop: 16 }}>
            <Mui.InputLabel>Selecione o Convênio</Mui.InputLabel>
            <Mui.Select
              value={selectedIds.convenioId}
              onChange={e => setSelectedIds(prev => ({ ...prev, convenioId: e.target.value }))}
              disabled={globalLoading || !selectedIds.contaId}
            >
              {convenios.map(convenio => (
                <Mui.MenuItem key={convenio.id} value={convenio.id}>
                  {convenio.nome}
                </Mui.MenuItem>
              ))}
            </Mui.Select>
          </Mui.FormControl>
        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Box p={1}>
            <Mui.Button
              size="large"
              onClick={handleGerarBoleto}
              disabled={globalLoading || !selectedIds.convenioId || loading.geracao}
              startIcon={loading.geracao && <Mui.CircularProgress size={20} />}
            >
              {loading.geracao ? 'Gerando...' : 'Gerar Boleto'}
            </Mui.Button>
          </Mui.Box>
          <Mui.Box p={1}>
            <Mui.Button size="large" onClick={onClose} disabled={globalLoading || loading.geracao}>
              Cancelar
            </Mui.Button>
          </Mui.Box>
        </Mui.DialogActions>
      </Mui.Dialog>
    </>
  );
};

export default ModalBoletoConvenio;
